/**
 * SI-04: 관리자 웹 무결성 검증 서비스
 * SRI(Subresource Integrity) 및 런타임 무결성 검사를 담당
 */

class IntegrityService {
  constructor() {
    this.expectedHashes = {};
    this.integrityViolations = [];
    this.checkInterval = null;
    this.integrityKey = null;
    this.keyLastFetched = null;
    this.keyRefreshInterval = 5 * 60 * 1000; // 5분마다 키 갱신 확인
    
    // 무결성 검사 설정
    this.config = {
      enableSRI: true,
      enableRuntimeCheck: true,
      enableDevToolsDetection: process.env.NODE_ENV === 'production', // 개발 환경에서는 비활성화
      checkIntervalMs: 30000, // 30초마다 검사
      maxViolations: 5,
      alertEndpoint: '/security/audit/log',
      keyRotationEndpoint: '/security/keys/current'
    };

    this.init();
  }

  /**
   * 백엔드에서 현재 무결성 키 가져오기 (KeyRotationService 통합)
   */
  async fetchIntegrityKey() {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v2';
      const response = await fetch(`${apiBaseUrl}${this.config.keyRotationEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include', // JWT 쿠키 포함
        body: JSON.stringify({
          // 관리자 웹에서는 암호화 불필요
          device_id: null,
          bundle_id: null
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      //console.log('IntegrityService: Key fetched from KeyRotationService', data);
      
      if (data.success && data.data && data.data.key) {
        this.integrityKey = data.data.key;
        this.keyLastFetched = Date.now();
        
        /*console.log('IntegrityService: Key fetched from KeyRotationService', {
          algorithm: data.data.algorithm,
          encrypted: data.data.encrypted,
          keyPreview: this.integrityKey.substring(0, 8) + '...'
        });*/
        
        return this.integrityKey;
      } else {
        throw new Error('Invalid response format from key rotation service');
      }
    } catch (error) {
      console.error('Failed to fetch integrity key from backend:', error);
      
      // Fallback to environment variables
      return this.getFallbackIntegrityKey();
    }
  }

  /**
   * 환경별 fallback 서명키 가져오기
   */
  getFallbackIntegrityKey() {
    console.log('IntegrityService: Using fallback environment key');
    
    const environment = process.env.REACT_APP_ENVIRONMENT || 'local';
    
    switch (environment) {
      case 'local':
      case 'development':
        return process.env.REACT_APP_INTEGRITY_KEY || 'dev-hmac-key-for-local-testing-only';
      
      case 'staging':
      case 'test':
        return process.env.REACT_APP_STAGING_INTEGRITY_KEY || 'staging-hmac-key';
      
      case 'production':
      case 'prod':
        return process.env.REACT_APP_PRODUCTION_INTEGRITY_KEY || 'production-hmac-key';
      
      default:
        console.warn(`Unknown environment: ${environment}, using production defaults`);
        return process.env.REACT_APP_PRODUCTION_INTEGRITY_KEY || 'production-hmac-key';
    }
  }

  /**
   * 키가 만료되었는지 확인하고 필요시 갱신
   */
  async ensureValidKey() {
    const now = Date.now();
    
    // 키가 없거나 갱신 주기가 지났으면 새로 가져오기
    if (!this.integrityKey || !this.keyLastFetched || 
        (now - this.keyLastFetched) > this.keyRefreshInterval) {
      
      console.log('IntegrityService: Refreshing integrity key');
      await this.fetchIntegrityKey();
    }
    
    return this.integrityKey;
  }

  /**
   * 서비스 초기화
   */
  async init() {
    try {
      // 무결성 키 가져오기
      await this.fetchIntegrityKey();
      
      // 예상 해시 로드
      await this.loadExpectedHashes();
      
      // 초기 무결성 검사
      await this.performInitialIntegrityCheck();
      
      // 주기적 검사 시작
      if (this.config.enableRuntimeCheck) {
        this.startPeriodicCheck();
      }

      // 페이지 언로드 시 정리
      window.addEventListener('beforeunload', () => {
        this.cleanup();
      });

      console.log('IntegrityService initialized successfully');
    } catch (error) {
      console.error('IntegrityService initialization failed:', error);
      this.reportViolation('INIT_FAILED', error.message);
    }
  }

  /**
   * 예상 해시 로드
   */
  async loadExpectedHashes() {
    try {
      // 빌드 시 생성된 매니페스트 파일에서 로드
      const response = await fetch('/integrity-manifest.json');
      if (response.ok) {
        const manifest = await response.json();
        
        // 개발 모드 체크
        if (manifest.integrity && manifest.integrity.developmentMode) {
          console.log('Development mode detected - integrity checks disabled');
          this.expectedHashes = {};
          return;
        }
        
        this.expectedHashes = manifest.resources || {};
        console.log(`Loaded ${Object.keys(this.expectedHashes).length} expected hashes`);
      } else {
        console.warn('Integrity manifest not found, using fallback');
        this.expectedHashes = this.getFallbackHashes();
      }
    } catch (error) {
      console.warn('Failed to load expected hashes:', error);
      this.expectedHashes = this.getFallbackHashes();
    }
  }

  /**
   * 초기 무결성 검사
   */
  async performInitialIntegrityCheck() {
    try {
      // 1. 스크립트 태그 무결성 검사
      await this.checkScriptIntegrity();
      
      // 2. 스타일시트 무결성 검사
      await this.checkStylesheetIntegrity();
      
      // 3. 중요 리소스 무결성 검사
      await this.checkCriticalResources();
      
      // 4. DOM 무결성 검사
      await this.checkDOMIntegrity();

      console.log('Initial integrity check completed');
    } catch (error) {
      console.error('Initial integrity check failed:', error);
      this.reportViolation('INITIAL_CHECK_FAILED', error.message);
    }
  }

  /**
   * 스크립트 무결성 검사
   */
  async checkScriptIntegrity() {
    const scripts = document.querySelectorAll('script[src]');
    
    for (const script of scripts) {
      try {
        const src = script.src;
        const relativePath = this.getRelativePath(src);
        
        // 외부 CDN 스크립트는 SRI 속성 확인
        if (this.isExternalResource(src)) {
          if (!script.integrity) {
            this.reportViolation('MISSING_SRI', `External script missing integrity: ${src}`);
          } else {
            console.log(`SRI verified for external script: ${src}`);
          }
        } else {
          // 내부 스크립트는 해시 검증
          const result = await this.verifyResourceHash(src, relativePath);
          if (!result.isValid) {
            this.reportViolation('SCRIPT_HASH_MISMATCH', 
              `Script integrity failed: ${relativePath}`);
          }
        }
      } catch (error) {
        console.warn(`Script integrity check failed for ${script.src}:`, error);
      }
    }
  }

  /**
   * 스타일시트 무결성 검사
   */
  async checkStylesheetIntegrity() {
    const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
    
    for (const stylesheet of stylesheets) {
      try {
        const href = stylesheet.href;
        const relativePath = this.getRelativePath(href);
        
        if (this.isExternalResource(href)) {
          if (!stylesheet.integrity) {
            this.reportViolation('MISSING_SRI', `External stylesheet missing integrity: ${href}`);
          }
        } else {
          const result = await this.verifyResourceHash(href, relativePath);
          if (!result.isValid) {
            this.reportViolation('STYLESHEET_HASH_MISMATCH', 
              `Stylesheet integrity failed: ${relativePath}`);
          }
        }
      } catch (error) {
        console.warn(`Stylesheet integrity check failed for ${stylesheet.href}:`, error);
      }
    }
  }

  /**
   * 중요 리소스 무결성 검사
   */
  async checkCriticalResources() {
    const criticalResources = [
      // Only include resources that actually exist and have expected hashes
      // '/static/js/main.js',
      // '/static/css/main.css',
      // '/manifest.json'
    ];

    for (const resource of criticalResources) {
      try {
        // 예상 해시가 정의된 리소스만 검증
        const expectedHash = this.expectedHashes[resource];
        if (!expectedHash) {
          console.log(`Skipping integrity check for ${resource}: no expected hash defined`);
          continue;
        }
        
        const result = await this.verifyResourceHash(resource, resource);
        if (!result.isValid) {
          this.reportViolation('CRITICAL_RESOURCE_COMPROMISED', 
            `Critical resource integrity failed: ${resource}`);
        }
      } catch (error) {
        console.warn(`Critical resource check failed for ${resource}:`, error);
      }
    }
  }

  /**
   * API 응답 헤더의 무결성 서명 검증
   */
  async verifyResponseIntegrity(response) {
    try {
      // 무결성 서명 헤더 확인
      let integrityHeaders = this.extractIntegrityHeaders(response);

      
      // 헤더에서 찾지 못한 경우 응답 본문의 _integrity 필드에서 추출
      if (!integrityHeaders && response.data && response.data._integrity) {
        integrityHeaders = {
          hash: response.data._integrity.hash,
          signature: response.data._integrity.signature,
          timestamp: response.data._integrity.timestamp,
          algorithm: response.data._integrity.algorithm,
          signed: response.data._integrity.signed
        };
        
        // 검증을 위해 _integrity 필드를 제거한 데이터 사용
        const { _integrity, ...dataWithoutIntegrity } = response.data;
        response.data = dataWithoutIntegrity;
      }
      
      if (!integrityHeaders) {
        //console.log('No integrity information found in response for URL:', response.config?.url);
        return { isValid: true, reason: 'NO_HEADERS' };
      }


      // 서명된 응답인지 확인
      if (!integrityHeaders.signed || integrityHeaders.signed !== 'true') {
        return { isValid: true, reason: 'NOT_SIGNED' }; // 서명되지 않은 응답은 통과
      }

      // 필수 헤더 확인
      if (!integrityHeaders.hash || !integrityHeaders.signature || !integrityHeaders.timestamp) {
        this.reportViolation('MISSING_INTEGRITY_HEADERS', 
          'Required integrity headers missing in API response');
        return { isValid: false, reason: 'MISSING_HEADERS' };
      }

      // 응답 데이터 해시 계산 (백엔드와 동일한 방식 - 정규화 없이 원본 데이터 사용)
      const responseData = JSON.stringify(response.data);
      const calculatedHash = await this.calculateSHA256(responseData);
      
      

      // 해시 검증
      if (calculatedHash !== integrityHeaders.hash) {
        this.reportViolation('RESPONSE_HASH_MISMATCH', 
          'API response hash verification failed');
        return { isValid: false, reason: 'HASH_MISMATCH' };
      }

      // 타임스탬프 검증 (5분 이내)
      const currentTime = Math.floor(Date.now() / 1000);
      const responseTime = parseInt(integrityHeaders.timestamp);
      const timeDiff = Math.abs(currentTime - responseTime);
      
      if (timeDiff > 300) { // 5분
        this.reportViolation('RESPONSE_TIMESTAMP_EXPIRED', 
          `API response timestamp too old: ${timeDiff}s`);
        return { isValid: false, reason: 'TIMESTAMP_EXPIRED' };
      }

      // 서명 검증 (실제 환경에서는 공개키로 검증)
      const isSignatureValid = await this.verifySignature(
        integrityHeaders.hash, 
        integrityHeaders.signature
      );

      if (!isSignatureValid) {
        this.reportViolation('RESPONSE_SIGNATURE_INVALID', 
          'API response signature verification failed');
        return { isValid: false, reason: 'SIGNATURE_INVALID' };
      }


      return { isValid: true, reason: 'VERIFIED' };

    } catch (error) {
      console.error('Response integrity verification error:', error);
      this.reportViolation('RESPONSE_INTEGRITY_ERROR', 
        `Response integrity check failed: ${error.message}`);
      return { isValid: false, reason: 'VERIFICATION_ERROR' };
    }
  }

  /**
   * HTTP 응답에서 무결성 헤더 추출
   */
  extractIntegrityHeaders(response) {
    try {
      const headers = response.headers || {};
      
      // 헤더에서 무결성 정보 추출
      const hash = headers['x-integrity-hash'] || headers['X-Integrity-Hash'];
      const signature = headers['x-integrity-signature'] || headers['X-Integrity-Signature'];
      const timestamp = headers['x-integrity-timestamp'] || headers['X-Integrity-Timestamp'];
      const algorithm = headers['x-integrity-algorithm'] || headers['X-Integrity-Algorithm'];
      const signed = headers['x-integrity-signed'] || headers['X-Integrity-Signed'];
      
      // 필수 헤더가 있는 경우에만 반환
      if (hash && signature && timestamp) {
        return {
          hash,
          signature,
          timestamp,
          algorithm: algorithm || 'sha256',
          signed: signed || 'true'
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error extracting integrity headers:', error);
      return null;
    }
  }

  /**
   * 서명 검증 (동적 키 사용)
   */
  async verifySignature(hash, signature) {
    try {
      // 최신 키 확인
      await this.ensureValidKey();
      
      const expectedSignature = await this.calculateHMAC(hash, this.integrityKey);
      const isValid = expectedSignature === signature;
      
      if (!isValid) {
        console.warn('Signature verification failed:', {
          environment: process.env.NODE_ENV,
          hostname: window.location.hostname,
          keyUsed: this.integrityKey.substring(0, 8) + '...',
          expectedSignature: expectedSignature.substring(0, 16) + '...',
          receivedSignature: signature.substring(0, 16) + '...'
        });
      }
      
      return isValid;
    } catch (error) {
      console.error('Signature verification error:', error);
      return false;
    }
  }

  /**
   * HMAC 계산
   */
  async calculateHMAC(data, key) {
    try {
      // HTTPS 환경 확인
      if (!window.crypto || !window.crypto.subtle) {
        console.warn('crypto.subtle not available for HMAC, using fallback');
        return this.fallbackHMAC(data, key);
      }

      const encoder = new TextEncoder();
      const keyData = encoder.encode(key);
      const dataBuffer = encoder.encode(data);
      
      const cryptoKey = await crypto.subtle.importKey(
        'raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
      );
      
      const signature = await crypto.subtle.sign('HMAC', cryptoKey, dataBuffer);
      return Array.from(new Uint8Array(signature))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    } catch (error) {
      console.error('HMAC calculation failed:', error);
      return this.fallbackHMAC(data, key);
    }
  }

  /**
   * Fallback HMAC (simple implementation for development)
   */
  fallbackHMAC(data, key) {
    // 간단한 해시 기반 HMAC 구현 (보안용 아님)
    const combined = key + data + key;
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    const result = Math.abs(hash).toString(16).padStart(8, '0');
    console.warn('Using fallback HMAC (not secure):', result);
    return result;
  }

  /**
   * DOM 무결성 검사
   */
  async checkDOMIntegrity() {
    try {
      // 중요 DOM 요소의 무결성 검사
      const criticalElements = [
        { selector: 'meta[name="viewport"]', attribute: 'content' },
        { selector: 'title', content: true },
        { selector: 'meta[charset]', attribute: 'charset' }
      ];

      for (const element of criticalElements) {
        const domElement = document.querySelector(element.selector);
        if (!domElement) {
          this.reportViolation('MISSING_CRITICAL_ELEMENT', 
            `Critical DOM element missing: ${element.selector}`);
        }
      }

      // 예상치 못한 스크립트 태그 감지
      const inlineScripts = document.querySelectorAll('script:not([src])');
      for (const script of inlineScripts) {
        if (script.textContent.trim() && !this.isWhitelistedInlineScript(script)) {
          this.reportViolation('UNEXPECTED_INLINE_SCRIPT', 
            'Unexpected inline script detected');
        }
      }

    } catch (error) {
      console.warn('DOM integrity check failed:', error);
    }
  }

  /**
   * 리소스 해시 검증
   */
  async verifyResourceHash(url, relativePath) {
    try {
      const expectedHash = this.expectedHashes[relativePath];
      if (!expectedHash) {
        return { isValid: false, error: 'No expected hash found' };
      }

      // 리소스 다운로드 및 해시 계산
      const response = await fetch(url);
      if (!response.ok) {
        return { isValid: false, error: 'Resource not found' };
      }

      const content = await response.text();
      const actualHash = await this.calculateSHA256(content);

      const isValid = actualHash === expectedHash.hash;
      
      return {
        isValid,
        expectedHash: expectedHash.hash,
        actualHash,
        error: isValid ? null : 'Hash mismatch'
      };

    } catch (error) {
      return { isValid: false, error: error.message };
    }
  }

  /**
   * SHA-256 해시 계산
   */
  async calculateSHA256(content) {
    try {
      // HTTPS 환경 확인
      if (!window.crypto || !window.crypto.subtle) {
        console.warn('crypto.subtle not available, falling back to alternative hash');
        return this.fallbackHash(content);
      }
      
      const encoder = new TextEncoder();
      const data = encoder.encode(content);
      
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const result = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      return result;
    } catch (error) {
      console.error('SHA-256 calculation failed:', error);
      console.error('Error stack:', error.stack);
      return this.fallbackHash(content);
    }
  }

  /**
   * 데이터 정규화 (백엔드와 동일한 방식)
   */
  normalizeData(data) {
    if (Array.isArray(data)) {
      return data.map(item => this.normalizeData(item));
    } else if (data !== null && typeof data === 'object') {
      const normalized = {};
      const sortedKeys = Object.keys(data).sort();
      for (const key of sortedKeys) {
        normalized[key] = this.normalizeData(data[key]);
      }
      return normalized;
    }
    return data;
  }

  /**
   * Fallback hash function (simple hash for development)
   */
  fallbackHash(content) {
    let hash = 0;
    if (content.length === 0) return hash.toString(16);
    
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    // Convert to positive hex string
    const hexHash = Math.abs(hash).toString(16).padStart(8, '0');
    console.warn('Using fallback hash (not secure):', hexHash);
    return hexHash;
  }

  /**
   * 주기적 무결성 검사 시작
   */
  startPeriodicCheck() {
    this.checkInterval = setInterval(async () => {
      try {
        await this.performRuntimeIntegrityCheck();
      } catch (error) {
        console.error('Periodic integrity check failed:', error);
      }
    }, this.config.checkIntervalMs);
  }

  /**
   * 런타임 무결성 검사
   */
  async performRuntimeIntegrityCheck() {
    try {
      // 새로 추가된 스크립트/스타일시트 검사
      await this.checkNewResources();
      
      // 메모리 내 코드 변조 감지
      await this.checkCodeTampering();
      
      // 개발자 도구 감지
      this.detectDevTools();

    } catch (error) {
      console.warn('Runtime integrity check failed:', error);
    }
  }

  /**
   * 새로운 리소스 검사
   */
  async checkNewResources() {
    // 현재 로드된 스크립트와 스타일시트 검사
    const scripts = document.querySelectorAll('script[src]');
    const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
    
    // 이전 검사와 비교하여 새로 추가된 리소스 감지
    // 실제 구현에서는 이전 상태를 저장하고 비교
    //console.log(`Checking ${scripts.length} scripts and ${stylesheets.length} stylesheets`);
  }

  /**
   * 코드 변조 감지
   */
  async checkCodeTampering() {
    try {
      // 중요 함수들의 무결성 검사
      const criticalFunctions = [
        'fetch',
        'XMLHttpRequest',
        'eval',
        'Function'
      ];

      for (const funcName of criticalFunctions) {
        if (window[funcName] && typeof window[funcName] === 'function') {
          const funcString = window[funcName].toString();
          // 함수가 변조되었는지 검사 (실제로는 더 정교한 검사 필요)
          if (funcString.includes('modified') || funcString.includes('hook')) {
            this.reportViolation('FUNCTION_TAMPERING', 
              `Critical function may be tampered: ${funcName}`);
          }
        }
      }
    } catch (error) {
      console.warn('Code tampering check failed:', error);
    }
  }

  /**
   * 개발자 도구 감지
   */
  detectDevTools() {
    // 개발 환경에서는 개발자 도구 감지 비활성화
    if (!this.config.enableDevToolsDetection) {
      return;
    }

    try {
      // 콘솔 크기 변화 감지
      const threshold = 160;
      if (window.outerHeight - window.innerHeight > threshold || 
          window.outerWidth - window.innerWidth > threshold) {
        this.reportViolation('DEVTOOLS_DETECTED', 'Developer tools may be open');
      }

      // 디버거 감지
      let devtools = false;
      const detector = () => {
        devtools = true;
      };
      
      console.log('%c', detector);
      if (devtools) {
        this.reportViolation('DEBUGGER_DETECTED', 'Debugger access detected');
      }

    } catch (error) {
      // 개발자 도구 감지 실패는 정상적인 상황일 수 있음
    }
  }

  /**
   * 무결성 위반 보고
   */
  reportViolation(type, message) {
    const violation = {
      type,
      message,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    this.integrityViolations.push(violation);
    
    console.warn('Integrity violation detected:', violation);

    // 서버로 위반 사항 전송
    this.sendViolationToServer(violation);

    // 임계값 초과 시 추가 조치
    if (this.integrityViolations.length >= this.config.maxViolations) {
      this.handleCriticalViolation();
    }
  }

  /**
   * 서버로 위반 사항 전송 (UC-04 V2 감사로그 시스템 연동)
   */
  async sendViolationToServer(violation) {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v2';
      
      // UC-04 V2 감사로그 형식으로 변환
      const auditLogEntry = {
        eventId: `EVT_${Date.now()}_${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
        eventType: 'INTEGRITY_VIOLATION',
        category: 'SYSTEM_SECURITY',
        severity: this.mapViolationToSeverity(violation.type),
        timestamp: Math.floor(new Date(violation.timestamp).getTime() / 1000),
        details: {
          violationType: violation.type,
          message: violation.message,
          url: violation.url,
          userAgent: violation.userAgent,
          source: 'admin-frontend'
        }
      };

      await fetch(`${apiBaseUrl}/security/audit/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(auditLogEntry)
      });
    } catch (error) {
      console.error('Failed to send violation to audit log system:', error);
    }
  }

  /**
   * 위반 유형을 심각도로 매핑
   */
  mapViolationToSeverity(violationType) {
    const severityMap = {
      'devtools': 'HIGH',
      'console_access': 'HIGH',
      'source_modification': 'CRITICAL',
      'network_tampering': 'CRITICAL',
      'memory_manipulation': 'CRITICAL',
      'debugger_detected': 'HIGH',
      'script_injection': 'CRITICAL'
    };
    
    return severityMap[violationType] || 'MEDIUM';
  }

  /**
   * 치명적 위반 처리
   */
  handleCriticalViolation() {
    console.error('Critical integrity violations detected. Taking protective measures.');
    
    // 사용자에게 경고 표시
    if (window.confirm('보안 위반이 감지되었습니다. 페이지를 새로고침하시겠습니까?')) {
      window.location.reload();
    }
  }

  /**
   * 유틸리티 메서드들
   */
  getRelativePath(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      return url;
    }
  }

  isExternalResource(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.origin !== window.location.origin;
    } catch {
      return false;
    }
  }

  isWhitelistedInlineScript(script) {
    // 허용된 인라인 스크립트 패턴 검사
    const whitelistedPatterns = [
      /^\/\*\s*webpack\s*\*\//,  // Webpack 관련
      /^\/\*\s*React\s*\*\//     // React 관련
    ];

    return whitelistedPatterns.some(pattern => 
      pattern.test(script.textContent.trim())
    );
  }

  getFallbackHashes() {
    // 매니페스트 로드 실패 시 사용할 기본 해시
    return {
      '/static/js/main.js': { hash: 'fallback-hash-js' },
      '/static/css/main.css': { hash: 'fallback-hash-css' }
    };
  }

  /**
   * 정리
   */
  cleanup() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * 무결성 상태 조회
   */
  getIntegrityStatus() {
    return {
      violations: this.integrityViolations,
      lastCheck: new Date().toISOString(),
      isHealthy: this.integrityViolations.length < this.config.maxViolations
    };
  }
}

// 전역 인스턴스 생성
window.IntegrityService = new IntegrityService();

export default IntegrityService;
